import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { IoCheckmark, IoClose } from 'react-icons/io5';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSession } from '@/contexts/LitSessionProvider';
import { toast } from 'sonner';
import useReferralCode from '@/hooks/onboarding/useReferralCode';
import { shortAddr, toSpecialFixed } from '@/lib/misc/utils';
import { useLocalStorage } from 'usehooks-ts';
import { ScrollArea } from '@/components/ui/scroll-area';
export default function ReferralModal({
  toggleModal
}: {
  toggleModal: () => void;
}) {
  const {
    PKP
  } = useSession();
  const [isCopied, setIsCopied] = useState(false);
  const [referralUrlCode, setReferralUrlCode] = useLocalStorage('referral-code', '');
  const {
    referralData
  } = useReferralCode(PKP, setReferralUrlCode);
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  const handleCopy = (textToCopy: string) => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      }).catch(() => {
        toast.error('Failed to copy!');
      });
    }
  };
  const referralLink = referralData && referralData.codes?.referral_code ? `https://tradegenius.com/home?code=${referralData?.codes?.referral_code}` : '';
  return <>
      <div className='w-full justify-center ta-c'>
        <div className='p-4 '>
          <div className='referalBanner bg- rounded-[12px] p-2 relative h-[210px] w-full flex items-center justify-center ta-c'>
            <IoClose onClick={toggleModal} className='cursor-pointer absolute right-[8px] top-[5px]' data-sentry-element="IoClose" data-sentry-source-file="ReferralModal.tsx" />
            <div className='w-full'>
              <p className='text-[26px] text-genius-cream m-0 tracking-[-1px]'>
                Refer Your Friends{' '}
              </p>

              <p className='text-[14px] text-genius-cream mt-8'>
                Refer your friends to earn rewards on Genius.
              </p>
            </div>
          </div>
          <div className='ta-l '>
            <div>
              <div className='relative w-full h-fit'>
                <Input ref={inputRef} className='mt-4 bg-genius-pink rounded-full w-full h-[38px] text-genius-blue' type='text' value={referralLink} readOnly data-sentry-element="Input" data-sentry-source-file="ReferralModal.tsx" />

                {isCopied ? <IoCheckmark className={'w-[15px] absolute top-[10px] text-green-600 right-4'} /> : <Copy onClick={() => handleCopy(referralLink)} className={'w-[15px] absolute top-[7px] right-4 cursor-pointer text-genius-blue'} />}
              </div>

              <div className='flex items-center justify-between gap-x-4 my-2'>
                <div className='px-2 w-1/4'>
                  <p className='text-genius-cream/50'>Referral Limit</p>
                  <p className='text-[24px] px-1'>
                    {referralData.codes && referralData.codes.remaining_uses ? <>
                        {referralData.codes.times_used}/
                        <span className='text-genius-cream/50'>
                          {referralData.codes.remaining_uses + referralData.codes.times_used}
                        </span>
                      </> : <span className='text-genius-cream/50'>0</span>}
                  </p>
                </div>
                <div className='relative w-full h-fit px-4'>
                  <div className='flex items-center justify-end ta-r p-2 px-4 mt-4 rounded-lg border-[1px] border-genius-blue text-genius-cream/50'>
                    <div className='flex flex-col text-sm'>
                      <div>Referee Volume</div>
                      <p className='text-genius-cream text-xl'>
                        $
                        {referralData && referralData?.totalReferralVolume ? toSpecialFixed(referralData?.totalReferralVolume?.totalVolume, 2).value : '0'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='w-full  px-6 mb-4'>
          <div className='bg-genius-purple GainsBox text-genius-cream/50 rounded-[20px] flex flex-col items-start justify-center border-genius-blue border-[1px]'>
            <div className='p-4 pb-0 h-[64px] '>
              <p className='flex items-center justify-start text-sm '>
                All your Points{' '}
                <div style={{
                filter: 'blur(1.5px)'
              }} className='text-genius-pink text-sm ml-2 blur-xs'>
                  1.3x
                </div>
              </p>
              <div style={{
              filter: 'blur(12px)'
            }} className='h-10 w-24 bg-genius-cream/40 opacity-50  mt-2 '></div>
            </div>
            <div className='flex items-center justify-center text-xs w-full mt-2 p-4 pb-0 h-[64px]'>
              <div className='w-1/3 flex flex-col items-start justify-start '>
                <p className='text-genius-cream/50'>Volume Points</p>
                <div style={{
                filter: 'blur(12px)'
              }} className='h-8 w-20 bg-genius-cream/40 opacity-50 rounded-lg  mt-2'></div>
              </div>
              <div className='w-1/3 flex flex-col items-start justify-start p-2'>
                <p className='text-genius-cream/50'>Deposit Points</p>
                <div style={{
                filter: 'blur(12px)'
              }} className='h-8 w-20 bg-genius-cream/40 opacity-50 rounded-lg  mt-2'></div>
              </div>
              <div className='w-1/3 flex flex-col items-start justify-start p-2'>
                <p className='text-genius-cream/50'>Referral Fees</p>
                <div style={{
                filter: 'blur(12px)'
              }} className='h-8 w-20 bg-genius-cream/40 opacity-50 rounded-lg  mt-2'></div>
              </div>
            </div>
          </div>
        </div>

        <ScrollArea className='h-[200px]' data-sentry-element="ScrollArea" data-sentry-source-file="ReferralModal.tsx">
          <table className='w-full mt-0'>
            <thead className='bg-genius-blue text-genius-cream'>
              <tr>
                <th className='w-[10%] py-4 text-left px-8'>#</th>
                <th className='w-[40%] py-4 ta-c'>Referral</th>
                <th className='w-[30%] py-4 ta-c'>Volume</th>
              </tr>
            </thead>
            <tbody>
              {referralData && referralData.referrals && referralData.referrals.length > 0 ? referralData.referrals.filter(x => {
              //remove duplicates
              return referralData.referrals.findIndex(y => y.referree_eth_address === x.referree_eth_address) === referralData.referrals.indexOf(x);
            }).map((item, index) => {
              const userVolume = referralData?.totalReferralVolume?.perUserVolume?.find(x => {
                return x.address?.toLowerCase() === item.referree_eth_address?.toLowerCase();
              });
              const volume = userVolume ? userVolume.volume : 0;
              return {
                ...item,
                volume
              };
            }).sort((a, b) => b.volume - a.volume).map((item, index) => <tr key={index} className='text-genius-cream'>
                      <td className='w-[10%] px-8'>{index + 1}</td>
                      <td className='px-8 py-4 w-[40%] text-center'>
                        {item.username ? item.username : shortAddr(item.referree_eth_address)}
                      </td>
                      <td className=' py-4 w-[30%] ta-c'>
                        ${toSpecialFixed(item.volume, 2).value}
                      </td>
                    </tr>) : <div className='w-[33vh] flex items-center justify-center'>
                  <td className='text-center py-4 text-genius-cream/50'>You haven't referred any users yet!</td>
                </div>}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </>;
}